/**
 * levelgen/generate.ts — Seed and difficulty in, playable level out.
 *
 * The whole framework, in the order a town gets built:
 *
 *   streets → landmarks → plots → parks → bins → trees → traffic → critters
 *           → tune to the difficulty band → validate → ship or throw away
 *
 * The two steps at the end are what make this a level *generator* rather than a
 * town *randomiser*:
 *
 * **Tune.** A budget is a starting point, not a guarantee. Two seeds built to
 * the same budget can rate a band apart, because where the seed put the bin
 * decides how far the errand is and how many streets it crosses — and those are
 * a third of the score. So the level is measured after it is built, and the two
 * knobs that can still move (how often the cars come, how much wildlife is out)
 * are turned until the measured band is the one that was asked for.
 *
 * **Validate.** If a seed produces a town with the bin walled in, or a tree
 * hanging over the pavement, it is discarded and the next seed is tried. Nothing
 * is repaired in place: a repair that half-works produces a subtly broken level,
 * and there is an infinite supply of seeds.
 *
 * ## Determinism
 *
 * Same seed and difficulty, same level, always — every stage draws from its own
 * named substream (see `rng.ts`), so this holds even as the individual stages
 * are changed. That is what makes a seed worth sharing and a bug report worth
 * filing.
 */
import { bandRange, budgetFor, rateLevel, type LevelBudget } from './difficulty';
import {
  binGreenClearance,
  greenSeparation,
  placeBins,
  placeLandmarks,
  plantTrees,
} from './landmarks';
import { makeRng, type Rng } from './rng';
import { planHazards, planTraffic } from './spawns';
import { cutLanes, placeParks, placePlots, planStreets, windStreets } from './town';
import { isPlayable, validateLevel } from './validate';
import { inflateRect } from '../level';
import type { Difficulty, LevelIssue, LevelSpec } from './types';
import { DIFFICULTIES } from './types';

export interface GenerateOptions {
  /** Anything — a word, a number, a UUID. Defaults to a time-based one. */
  seed?: string;
  difficulty?: Difficulty;
  /**
   * How many seeds to try before giving up. Each attempt is a fresh town, so
   * this is the number of *different* levels considered, not retries of one.
   */
  attempts?: number;
}

/** Thrown when no attempt produced a playable level. Carries the last verdict. */
export class LevelGenerationError extends Error {
  constructor(
    message: string,
    readonly issues: readonly LevelIssue[],
  ) {
    super(message);
    this.name = 'LevelGenerationError';
  }
}

/** What one run of the generator produced, whether or not it succeeded. */
export interface GenerateResult {
  level: LevelSpec | null;
  /** The verdict on the last attempt — empty only if a level came back clean. */
  issues: LevelIssue[];
  /** How many towns were built before one passed (or the budget ran out). */
  attempts: number;
}

/**
 * Generate a level, or explain why not.
 *
 * The non-throwing form. Useful in tests and tooling, where a failure is
 * information rather than an exception.
 */
export function tryGenerateLevel(options: GenerateOptions = {}): GenerateResult {
  const seed = options.seed ?? `auto-${Date.now().toString(36)}`;
  const difficulty = options.difficulty ?? 'medium';
  const attempts = options.attempts ?? 12;

  let issues: LevelIssue[] = [
    { code: 'no-attempts', severity: 'error', message: 'no attempts were made' },
  ];

  for (let attempt = 0; attempt < attempts; attempt++) {
    // The seed of record stays the one the caller gave; the attempt index only
    // perturbs the stream, so `seed` still identifies the level that shipped.
    const draft = build(makeRng(`${seed}#${attempt}`), seed, difficulty);
    if (!draft) {
      issues = [
        {
          code: 'no-layout',
          severity: 'error',
          message: 'the street plan left nowhere to put the bin, the home pad or the walker',
        },
      ];
      continue;
    }

    issues = validateLevel(draft);
    if (isPlayable(issues)) return { level: draft, issues, attempts: attempt + 1 };
  }

  return { level: null, issues, attempts };
}

/**
 * Generate a level, or throw.
 *
 * The form the game uses. A level that cannot be generated is not something a
 * caller can sensibly carry on from, so the failure is loud and carries the
 * validator's findings with it.
 */
export function generateLevel(options: GenerateOptions = {}): LevelSpec {
  const { level, issues, attempts } = tryGenerateLevel(options);
  if (level) return level;

  const summary = issues
    .filter((i) => i.severity === 'error')
    .map((i) => `${i.code}: ${i.message}`)
    .join('; ');
  throw new LevelGenerationError(
    `could not generate a ${options.difficulty ?? 'medium'} level from seed ` +
      `"${options.seed ?? '(auto)'}" in ${attempts} attempts — ${summary}`,
    issues,
  );
}

/**
 * One level per difficulty from a single seed — a campaign, in order.
 *
 * Each band gets its own derived seed rather than the same one, so the four
 * levels are four different towns. Sharing the seed would give four towns with
 * the same street plan and different traffic on it, which reads as one level
 * played four times.
 */
export function generateLevelSet(seed: string, attempts?: number): Record<Difficulty, LevelSpec> {
  const out = {} as Record<Difficulty, LevelSpec>;
  for (const difficulty of DIFFICULTIES) {
    out[difficulty] = generateLevel({ seed: `${seed}:${difficulty}`, difficulty, attempts });
  }
  return out;
}

/**
 * Build one candidate town. Returns `null` when the layout left nowhere to put
 * a landmark — an unlucky seed rather than a bug, and the caller's cue to move
 * on to the next one.
 */
function build(rng: Rng, seed: string, difficulty: Difficulty): LevelSpec | null {
  const budget = budgetFor(difficulty);

  // Lanes are their own stage, on their own substream. They come after the
  // streets because they are cut into the ground the streets leave behind, and
  // before the landmarks because a lane changes what a block *is* — the bin
  // wants to be placed against the town it will actually stand in.
  const plan = cutLanes(
    rng.fork('lanes'),
    windStreets(
      rng.fork('winding'),
      // The surround is grown as the grid is laid rather than bolted on after,
      // so the streets run straight through it — see `surround.ts`. Its own
      // substream, like every other stage, or adding a ring block would shift
      // the street sequence and silently rebuild every town in every test.
      planStreets(rng.fork('streets'), budget, rng.fork('surround')),
      budget,
    ),
    budget,
  );
  // Landmarks before houses: the walk is what the level is *for*, so it gets
  // first refusal on the ground, and the town is built around it.
  const landmarks = placeLandmarks(rng.fork('landmarks'), plan, budget);
  if (!landmarks) return null;
  const buildings = placePlots(rng.fork('plots'), plan, budget, landmarks.reserved);

  // Parks come after the landmarks so they can be kept off them: the home pad
  // is drawn above the ground for the whole run, and grass laid underneath it
  // is two textured slabs on one patch, z-fighting the entire time. The greens
  // the landmarks reserved are laid first and are what make the level finishable
  // at all — the dog will only go on grass.
  //
  // Home is reserved out to the **whole doorstep distance**, not to its own
  // edge: a spare green laid behind the house would put the walk back on the
  // doorstep by a route the landmark search never sees, and the validator would
  // then throw the seed away for `grass-on-the-doorstep` after a whole town had
  // been built. Squarer than the rule the validator applies (which measures the
  // real gap), so it can only ever refuse ground the validator would too.
  //
  // The errand bin is reserved the same way and for the mirror-image reason: it
  // used to hold off only its own drop zone, which left leftover ground right
  // beside it free for a green — and a green at the bin is the back half of the
  // errand deleted (`bin-beside-the-grass`). See `binGreenClearance`.
  const parks = placeParks(
    rng.fork('parks'),
    plan,
    buildings,
    [
      inflateRect(landmarks.home, greenSeparation(plan.bounds)),
      inflateRect({ ...landmarks.bin, w: 0, h: 0 }, binGreenClearance(plan.bounds)),
      ...landmarks.greens,
    ],
    budget,
    landmarks.greens,
  );
  // The rest of the town's bins, last of all: they stand against the finished
  // frontages and have to be kept clear of the finished parks, so they need
  // both to exist first. See `placeBins` for why one bin per town was a search
  // rather than a walk.
  const bins = placeBins(
    plan,
    buildings,
    parks,
    landmarks,
    rng.fork('bin-count').int(...budget.bins),
  );
  const trees = plantTrees(rng.fork('trees'), plan, buildings, parks, landmarks, bins);

  const level: LevelSpec = {
    seed,
    difficulty,
    world: plan.world,
    bounds: plan.bounds,
    roads: plan.roads,
    buildings,
    parks,
    trees,
    bins,
    home: landmarks.home,
    start: landmarks.start,
    traffic: planTraffic(rng.fork('traffic'), plan.roads, plan.world, plan.bounds, budget),
    hazards: planHazards(
      rng.fork('hazards'),
      plan.bounds,
      buildings,
      landmarks.start,
      budget,
    ),
    // Filled in by `tuneToBand` below, which is what finally decides whether
    // this level is the difficulty it claims to be.
    rating: { score: 0, difficulty, parts: { traffic: 0, critters: 0, journey: 0, crossings: 0, town: 0 } },
  };

  tuneToBand(rng, level, budget);
  return level;
}

/** How far the tuner may push each knob before admitting the seed is wrong. */
const GAP_SCALE_RANGE: [number, number] = [0.5, 2.5];
const EXTRA_CRITTER_RANGE: [number, number] = [-2, 5];

/**
 * Turn the two continuous knobs until the level rates as the band it claims,
 * rewriting its traffic, its wildlife and its rating in place.
 *
 * Traffic frequency and critter count, and nothing else. The fixed parts of the
 * score — how big the town is, how long the errand, how many crossings — are
 * baked in by the time this runs, and changing them would mean rebuilding the
 * town, which is what a fresh attempt is for.
 *
 * If the knobs run out of travel before the band is reached, the rating is left
 * as it lands. The validator then rejects the level for `difficulty-mismatch`
 * and the next seed is tried, which is the honest outcome: this seed's town
 * cannot be made into that difficulty. **It does not throw**, because "this
 * town won't stretch that far" is a fact about the town, not an error.
 *
 * Exported because it is also the operation you want after editing a level by
 * hand: change where the bin goes and the errand changes, so the traffic has to
 * be re-dealt before the label means anything again.
 */
export function tuneToBand(rng: Rng, level: LevelSpec, budget: LevelBudget): void {
  // Only the lower edge is needed: if the level is out of band at all, it is
  // either below `lo` (too easy) or above the band entirely (too hard).
  const [lo] = bandRange(level.difficulty);
  let gapScale = 1;
  let extra = 0;

  for (let step = 0; step < 16; step++) {
    level.rating = rateLevel(level);
    if (level.rating.difficulty === level.difficulty) return;

    const before: [number, number] = [gapScale, extra];
    if (level.rating.score < lo) {
      // Too easy: more traffic, more wildlife.
      gapScale = clampTo(gapScale * 0.85, GAP_SCALE_RANGE);
      extra = clampTo(extra + 1, EXTRA_CRITTER_RANGE);
    } else {
      gapScale = clampTo(gapScale * 1.18, GAP_SCALE_RANGE);
      extra = clampTo(extra - 1, EXTRA_CRITTER_RANGE);
    }
    // Both knobs at their stop and still out of band — no further move exists.
    if (gapScale === before[0] && extra === before[1]) break;

    // Rebuilt from freshly forked streams rather than continuing the old ones,
    // so a given (gapScale, extra) always produces the same traffic and the
    // same critters however many steps it took to get there.
    level.traffic = planTraffic(
      rng.fork('traffic'),
      level.roads,
      level.world,
      level.bounds,
      budget,
      gapScale,
    );
    level.hazards = planHazards(
      rng.fork('hazards'),
      level.bounds,
      level.buildings,
      level.start,
      budget,
      extra,
    );
  }

  level.rating = rateLevel(level);
}

function clampTo(v: number, [lo, hi]: [number, number]): number {
  return Math.min(hi, Math.max(lo, v));
}
